/**
 * Mega-menu configuration for site navigation.
 *
 * Icons come from /components/BrandIcons.jsx — custom Helios glyphs
 * (Palantir / DeepMind / Anthropic visual logic). NO template / lucide icons.
 */
import React from "react";
import {
  HeliosIcon, BrainIcon, FactoryIcon, FrameworkIcon,
  PaperIcon, LabNoteIcon, QuestionIcon,
  BankIcon, SovereignIcon, HealthIcon, EnergyIcon, ManufactureIcon, TelecomIcon, RealEstateIcon,
  AboutIcon, TeamIcon, EuropeIcon, TrustIcon, CareersIcon,
  GovernanceIcon, ReasoningIcon, OptimiseIcon, KnowledgeIcon,
  MaritimeIcon, DefenseIcon, SpaceIcon, RoboticsIcon, PhysicalIcon, InsuranceIcon, RetailIcon, PublicSectorIcon, DiscoveryIcon, EmbedIcon,
} from "../components/BrandIcons.jsx";

const wrap = (Cmp) => <Cmp size={22} />;

const I = {
  helios:    wrap(HeliosIcon),
  brain:     wrap(BrainIcon),
  factory:   wrap(FactoryIcon),
  framework: wrap(FrameworkIcon),
  paper:     wrap(PaperIcon),
  beaker:    wrap(LabNoteIcon),
  question:  wrap(QuestionIcon),
  shield:    wrap(GovernanceIcon),
  reasoning: wrap(ReasoningIcon),
  building:  wrap(AboutIcon),
  trust:     wrap(TrustIcon),
  europe:    wrap(EuropeIcon),
  team:      wrap(TeamIcon),
  briefcase: wrap(CareersIcon),
  // industries
  bank:      wrap(BankIcon),
  sovereign: wrap(SovereignIcon),
  health:    wrap(HealthIcon),
  energy:    wrap(EnergyIcon),
  factory2:  wrap(ManufactureIcon),
  telecom:   wrap(TelecomIcon),
  realestate:wrap(RealEstateIcon),
  insurance: wrap(InsuranceIcon),
  retail:    wrap(RetailIcon),
  publicsector: wrap(PublicSectorIcon),
  maritime:  wrap(MaritimeIcon),
  defense:   wrap(DefenseIcon),
  space:     wrap(SpaceIcon),
  robotics:  wrap(RoboticsIcon),
  physical:  wrap(PhysicalIcon),
};

export const MEGAMENU = {
  "/partners": {
    title: "The Helios Partner Network",
    subtitle: "Build the bridge. Bring the intelligence.",
    columns: [
      { heading: "Partnership paths", items: [
        { label: "System Integrators", desc: "Connect, configure and deliver", path: "/partners/system-integrators", icon: wrap(FrameworkIcon) },
        { label: "Embedded World Models", desc: "Living intelligence inside your software", path: "/partners/embedded-world-models", icon: wrap(EmbedIcon) }
      ] },
      { heading: "Build together", items: [
        { label: "Partner Network", desc: "A shared platform. A wider ambition.", path: "/partners", icon: wrap(TeamIcon) },
        { label: "Become a partner", desc: "Start with one useful outcome", path: "/partners#partner-enquiry", icon: wrap(DiscoveryIcon) }
      ] }
    ]
  },
  "/product": {
    title: "Helios products",
    subtitle: "Understand your world. Find the path. Build your own.",
    cta: { label: "Explore products", path: "/product" },
    columns: [
      {
        heading: "Foundation",
        items: [
          { code: "P-01", label: "Helios",         desc: "Understand your organization",                    path: "/product/helios",  icon: I.helios },
        ],
      },
      {
        heading: "Managed",
        items: [
          { code: "P-02", label: "Helios Brain",   desc: "Find your best achievable path",                        path: "/product/brain",   icon: I.brain },
        ],
      },
      {
        heading: "Self-serve",
        items: [
          { code: "P-03", label: "Helios Factory", desc: "Build your own · Planned September 2026",     path: "/product/factory", icon: I.factory },
        ],
      },
      {
        heading: "The framework",
        items: [
          { code: "·", label: "H·E·L·I·O·S",     desc: "Our six disciplines, one method",       path: "/framework",       icon: I.framework },
        ],
      },
    ],
  },

  "/research": {
    title: "Research",
    subtitle: "Papers, lab notes, and open questions",
    cta: { label: "Open research feed", path: "/research" },
    columns: [
      {
        heading: "Publications",
        items: [
          { label: "Papers",            desc: "Technical reports & specs",          path: "/research?tab=papers",    icon: I.paper },
          { label: "Lab notes",         desc: "Dated entries from Athens",          path: "/research?tab=notes",     icon: I.beaker },
          { label: "Open questions",    desc: "What we are working on",             path: "/research?tab=questions", icon: I.question },
        ],
      },
      {
        heading: "Active projects",
        items: [
          { label: "Project Athena",    desc: "Adversarial governance",             path: "/research", icon: I.shield },
          { label: "Project Demeter",   desc: "Causal identification",              path: "/research", icon: I.helios },
          { label: "Project Hephaestus",desc: "Substrate engineering",              path: "/research", icon: I.factory },
        ],
      },
    ],
  },

  "/industries": {
    title: "Industries",
    subtitle: "Fifteen industries. Real questions. A way forward.",
    cta: { label: "Browse all", path: "/industries" },
    columns: [
      {
        heading: "Finance",
        items: [
          { label: "Banking",        desc: "Credit, capital, treasury",         path: "/industries/banking",         icon: I.bank },
          { label: "Sovereign funds",desc: "Multi-decade portfolio modelling",  path: "/industries/sovereign-wealth",icon: I.sovereign },
          { label: "Insurance",      desc: "Risk pricing & reserving",          path: "/industries/insurance",       icon: I.insurance },
        ],
      },
      {
        heading: "Public & sovereign",
        items: [
          { label: "Public sector",  desc: "Ministries, regulators, cities",    path: "/industries/public-sector",   icon: I.publicsector },
          { label: "Healthcare",     desc: "Capacity, pathways, outcomes",      path: "/industries/healthcare",      icon: I.health },
          { label: "Defense",        desc: "Capability & force structure",      path: "/industries/defense",         icon: I.defense },
        ],
      },
      {
        heading: "Industry & real sector",
        items: [
          { label: "Energy",         desc: "Grids, generation, curtailment",    path: "/industries/energy",          icon: I.energy },
          { label: "Manufacturing",  desc: "Throughput & exception management", path: "/industries/manufacturing",   icon: I.factory2 },
          { label: "Maritime",       desc: "Fleet, routes, ports",              path: "/industries/maritime",        icon: I.maritime },
          { label: "Telecom",        desc: "Network planning & ops",            path: "/industries/telecommunications", icon: I.telecom },
          { label: "Real estate",    desc: "Portfolio & development",           path: "/industries/real-estate",     icon: I.realestate },
          { label: "Retail",         desc: "Pricing, network, loyalty",         path: "/industries/retail",          icon: I.retail },
        ],
      },
      {
        heading: "Frontier domains",
        items: [
          { label: "Space",          desc: "Constellations, missions, orbits",  path: "/industries/space",           icon: I.space },
          { label: "Robotics",       desc: "Autonomy & fleet deployment",       path: "/industries/robotics",        icon: I.robotics },
          { label: "Physical AI",    desc: "Embodied / sim-to-real",            path: "/industries/physical",        icon: I.physical },
        ],
      },
    ],
  },

  "/company": {
    title: "Company",
    subtitle: "An Applied Intelligence Lab from Athens",
    cta: { label: "About Helios Brain", path: "/company" },
    columns: [
      {
        heading: "About",
        items: [
          { label: "Founder letter",  desc: "Why we are building this",          path: "/company#letter",   icon: I.building },
          { label: "Team",            desc: "The people behind Helios",              path: "/company#team", icon: I.team },
        ],
      },
      {
        heading: "Position",
        items: [
          { label: "Europe",          desc: "Why we build here, for here",       path: "/europe",  icon: I.europe },
          { label: "Trust",           desc: "Compliance, residency, disclosure", path: "/trust",   icon: I.trust },
          { label: "Trust Center",    desc: "Full compliance & sovereign infra", path: "/trust-center", icon: I.trust },
        ],
      },
      {
        heading: "Operate",
        items: [
          { label: "Careers",         desc: "Open roles in Athens",              path: "/careers", icon: I.briefcase },
          { label: "Imprint",         desc: "Legal entity & registration",       path: "/imprint", icon: I.paper },
        ],
      },
    ],
  },
};
